// Mirrors NameItApp/components/Mascot/mascotMood.ts. Keep the thresholds and
// face definitions identical so web and mobile players see the same mascot
// at the same second of a round.

export function moodForTimer(secondsLeft, totalSeconds) {
  if (secondsLeft <= 0) return "timesup";
  if (secondsLeft <= Math.min(10, totalSeconds * 0.2)) return "panic";
  if (secondsLeft / totalSeconds <= 0.35) return "scared";
  if (secondsLeft / totalSeconds <= 0.5) return "nervous";
  return "focused";
}

// Mood for the mascot peeking over the in-round letter card.
export function roundMood({ secondsLeft, totalSeconds, submitted }) {
  if (submitted && secondsLeft > 0) return "happy";
  if (totalSeconds - secondsLeft < 2) return "celebrate";
  return moodForTimer(secondsLeft, totalSeconds);
}

// a/b = outer/inner body motion, hands/flap/sink only apply when peeking.
export const FACES = {
  calm:       { eye: "open", mouth: "smile", blink: true, look: "around", a: "bob-slow", hands: "rest" },
  happy:      { eye: "happy", mouth: "smile", fx: ["blush"], a: "bob", hands: "rest" },
  focused:    { eye: "open", brow: "determined", mouth: "tongue", blink: true, look: "around", a: "bob-slow", b: "tilt", hands: "rest" },
  nervous:    { eye: "open", brow: "worried", mouth: "wavy", fx: ["sweat1"], blink: true, a: "bob-fast", b: "jitter", hands: "tap" },
  scared:     { eye: "wide", brow: "scared", mouth: "grimace", fx: ["sweat2", "pale"], b: "tremble", hands: "tap", flap: "up" },
  panic:      { eye: "tiny", brow: "panic", mouth: "scream", fx: ["sweat3", "pale"], dart: true, a: "hop", b: "shake", hands: "shake", flap: "flutter" },
  timesup:    { eye: "shock", brow: "shock", mouth: "o", fx: ["burst2", "pale"], a: "jolt", hands: "slip", flap: "up", sink: true },
  celebrate:  { eye: "happy", mouth: "grin", fx: ["blush", "burst", "sparkles"], a: "jump", hands: "rest" },
  suspicious: { eye: "half", brow: "skeptic", mouth: "smirk", look: "side", b: "lean", hands: "rest" },
  sad:        { eye: "sad", brow: "worried", mouth: "frown", fx: ["tear"], blink: true, a: "droop", b: "sway", hands: "rest" },
  impatient:  { eye: "half", brow: "determined", mouth: "flat", look: "side", a: "tap", hands: "tap" },
  thinking:   { eye: "open", mouth: "o", look: "around", blink: true, dots: true, a: "bob-slow" },
  // Playground-only faces.
  dizzy:      { eye: "dizzy", mouth: "wavy", b: "sway" },
  squish:     { eye: "happy", mouth: "grin", fx: ["blush"] },
  whee:       { eye: "shock", brow: "shock", mouth: "scream" },
};

export const PALETTES = {
  red:   { top: "#f24b4b", bot: "#d41f27", lip: "#9f1520", flap: "#ff8f8f" },
  green: { top: "#34d399", bot: "#10b981", lip: "#047857", flap: "#a7f3d0" },
  amber: { top: "#fcd34d", bot: "#f59e0b", lip: "#b45309", flap: "#fde68a" },
  berry: { top: "#e0457b", bot: "#be185d", lip: "#831843", flap: "#f59ab9" },
  orange: { top: "#fb923c", bot: "#ea580c", lip: "#9a3412", flap: "#fed7aa" },
  teal:  { top: "#2dd4bf", bot: "#0d9488", lip: "#115e59", flap: "#99f6e4" },
  blue:  { top: "#60a5fa", bot: "#2563eb", lip: "#1e3a8a", flap: "#bfdbfe" },
  purple: { top: "#a78bfa", bot: "#7c3aed", lip: "#4c1d95", flap: "#ddd6fe" },
  grey:  { top: "#e7e5e4", bot: "#c4bfbc", lip: "#a8a29e", flap: "#f5f5f4" },
};

export const BOT_LOOKS = {
  kafi: {
    color: "green",
    accessory: "sprout",
    faces: {
      idle:     { eye: "happy", mouth: "smile", fx: ["blush"], a: "bob-slow" },
      thinking: { eye: "open", mouth: "o", look: "around", dots: true, blink: true, a: "bob-slow" },
      won:      { eye: "happy", mouth: "grin", fx: ["blush", "sparkles"], a: "jump" },
      lost:     { eye: "sad", mouth: "smile", fx: ["blush"], a: "droop", b: "sway" },
    },
  },
  kosi: {
    color: "amber",
    accessory: "band",
    faces: {
      idle:     { eye: "open", brow: "determined", mouth: "grin", blink: true, a: "bob" },
      thinking: { eye: "open", brow: "determined", mouth: "tongue", look: "around", dots: true, blink: true, b: "jitter" },
      won:      { eye: "happy", mouth: "grin", fx: ["burst"], a: "jump" },
      lost:     { eye: "sad", brow: "worried", mouth: "grimace", fx: ["tear"], a: "droop" },
    },
  },
  kemi: {
    color: "berry",
    accessory: "crown",
    faces: {
      idle:     { eye: "half", brow: "skeptic", mouth: "smirk", lashes: true, a: "bob-slow" },
      thinking: { eye: "half", brow: "skeptic", mouth: "smirk", lashes: true, look: "side", dots: true },
      won:      { eye: "half", brow: "skeptic", mouth: "grin", lashes: true, fx: ["sparkles"], b: "lean" },
      lost:     { eye: "shock", brow: "shock", mouth: "o", lashes: true, fx: ["pale", "burst2"], a: "jolt" },
    },
  },
};

export const PENCIL_FACES = {
  idle:     { eye: "open", mouth: "smile", blink: true, a: "bob-slow" },
  thinking: { eye: "open", mouth: "o", look: "around", dots: true, blink: true, a: "bob-slow" },
  judging:  { eye: "half", brow: "skeptic", mouth: "smirk", look: "side", b: "lean" },
  happy:    { eye: "happy", mouth: "grin", fx: ["sparkles"], a: "jump" },
  sad:      { eye: "sad", brow: "worried", mouth: "frown", a: "droop" },
};

// useId() output isn't safe inside url(#…) references.
export const safeId = (id) => id.replace(/[^a-zA-Z0-9_-]/g, "");

// A player's own mascot ("Your Namey"): a base, a colour and a resting face.
// Saved to users/{uid}.mascot so web and mobile show the same one. It is only
// ever shown to its owner.
export const MASCOT_BASES = [
  { id: "pencil", name: "Pencil" },
  { id: "paper", name: "Paper" },
];

// Curated so the face stays readable and the shading works on both bases.
export const MASCOT_COLORS = ["red", "orange", "amber", "teal", "blue", "purple", "berry"];

// Resting faces; timer and reaction moods still take over during play.
export const PERSONALITY_FACES = {
  cheerful:   { label: "Cheerful", face: { eye: "open", mouth: "smile", blink: true, look: "around", a: "bob-slow", hands: "rest" } },
  cool:       { label: "Cool", face: { eye: "half", brow: "skeptic", mouth: "smirk", a: "bob-slow", hands: "rest" } },
  determined: { label: "Determined", face: { eye: "open", brow: "determined", mouth: "grin", blink: true, a: "bob", hands: "rest" } },
};

export const DEFAULT_MASCOT = { base: "pencil", color: "red", face: "cheerful" };

export function normalizeMascot(m) {
  // "green" was dropped from the picker as too close to teal.
  if (m?.color === "green") m = { ...m, color: "teal" };
  return {
    base: MASCOT_BASES.some((b) => b.id === m?.base) ? m.base : DEFAULT_MASCOT.base,
    color: MASCOT_COLORS.includes(m?.color) ? m.color : DEFAULT_MASCOT.color,
    face: PERSONALITY_FACES[m?.face] ? m.face : DEFAULT_MASCOT.face,
  };
}

// The game's own characters, as mascot descriptors.
export const CAST = {
  namey: { base: "pencil", color: "red", face: "cheerful" },
  letty: { base: "paper", color: "red", face: "cheerful" },
  kafi: { base: "paper", color: "green", face: "cheerful", accessory: "sprout" },
  kosi: { base: "paper", color: "amber", face: "determined", accessory: "band" },
  kemi: { base: "paper", color: "berry", face: "cool", accessory: "crown" },
};

// One word per letter for the playground's "M is for Mango!" reaction.
export const LETTER_WORDS = {
  A: "Apple", B: "Banana", C: "Cat", D: "Drum", E: "Egg", F: "Fish", G: "Goat",
  H: "Hat", I: "Igloo", J: "Jam", K: "Kite", L: "Lion", M: "Mango", N: "Nest",
  O: "Owl", P: "Panda", Q: "Queen", R: "Rice", S: "Shark", T: "Tiger",
  U: "Umbrella", V: "Violin", W: "Whale", X: "Xylophone", Y: "Yam", Z: "Zebra",
};

// How a player's mascot reacts to the points one answer earned.
export function scoreMood(valid, points) {
  if (!valid || points <= 0) return "sad";
  if (points >= 8) return "celebrate";
  if (points >= 4) return "happy";
  return "calm";
}

const STATIC_FX = ["blush", "pale"];

// The same expression with every looping animation removed.
export function stillFace(face) {
  return {
    ...face,
    a: undefined,
    b: undefined,
    blink: false,
    look: undefined,
    dart: false,
    dots: false,
    flap: face.flap === "flutter" ? "up" : face.flap,
    fx: face.fx?.filter((f) => STATIC_FX.includes(f)),
  };
}
